import type { RecognizedDocument } from "@/api/schemas";
import type { ExtractedField } from "./useExtractionStore";

/**
 * R2 — hydration source: recognized-document metadata → project form fields.
 *
 * The recognizer already reads the ured (name+address), the architect
 * (name + HKA number) and the investitor from uploaded admin documents;
 * until R2 those values only decorated the source cards while the form
 * showed "[ZA RUČNI UNOS]". This module maps them onto ``ProjectDataInput``
 * field names so the EXISTING prefill path (``prefillToDraft`` +
 * ``mergePrefill``) can hydrate the draft — a new SOURCE, zero new merge
 * rules.
 *
 * Trust model: every document-sourced value is capped at confidence 0.69 —
 * below the 0.75 stamp threshold — so the form always shows the amber
 * "provjeri" stamp ("iz dokumenta" in the tooltip). The human confirms;
 * nothing document-sourced ever silently rides into generation as green.
 */

/** Cap that forces the amber "provjeri" ConfidenceStamp (< 0.75). */
export const DOC_CONFIDENCE_CAP = 0.69;

/** recognized field name → ProjectDataInput field name (direct fills). */
const DIRECT_FIELD_MAP: Record<string, string> = {
  ured_naziv: "ured_name",
  ured_sjediste: "ured_address",
  arhitekt_ime: "glavni_projektant_name",
  hka_broj: "glavni_projektant_license",
  investitor_naziv: "investitor_name",
  investitor_oib: "investitor_oib",
  investitor_adresa: "investitor_address",
  katastarska_cestica: "katastarska_cestica",
  katastarska_opcina: "katastarski_opcina",
  povrsina_cestice: "cestica_povrsina_m2",
};

/** ZK-owner → investitor is a HYPOTHESIS (the owner usually is the
 * investor, but not always — the recognizer itself refuses to conflate
 * them). Offered only as a low-confidence suggestion with an explicit
 * note, and only when no document named the investitor directly. */
const VLASNIK_FIELD_MAP: Record<string, string> = {
  vlasnik_naziv: "investitor_name",
  vlasnik_oib: "investitor_oib",
  vlasnik_adresa: "investitor_address",
};

const VLASNIK_NOTE =
  "Vlasnik iz ZK izvatka — provjerite je li ujedno investitor.";

/** Recognizer vraća vlasnike kao LISTE kad ima više suvlasnika (stvarni ZK
 *  Buzet: 2 suvlasnika). Forma ima singularna investitor_* polja, pa lista
 *  nikad ne smije ući kao array — imena spajamo čitljivo, ostala polja
 *  (OIB/adresa) uzimaju prvog uz napomenu o suvlasnicima. */
function scalarizeOwnerName(v: unknown): { value: unknown; multi: number } {
  if (Array.isArray(v)) {
    const names = v.filter((x): x is string => typeof x === "string" && x.trim().length > 0).map((x) => x.trim());
    if (names.length === 0) return { value: null, multi: 0 };
    if (names.length === 1) return { value: names[0], multi: 1 };
    return { value: `${names.join("; ")} (${names.length} suvlasnika)`, multi: names.length };
  }
  return { value: v, multi: typeof v === "string" && v.trim() ? 1 : 0 };
}

function firstOfList(v: unknown): unknown {
  if (Array.isArray(v)) return v.find((x) => x != null && x !== "") ?? null;
  return v;
}

function fieldValue(doc: RecognizedDocument, key: string): unknown {
  const f = doc.fields?.[key];
  return f && f.value != null && f.value !== "" ? f.value : null;
}

function fieldConfidence(doc: RecognizedDocument, key: string): number {
  const c = doc.fields?.[key]?.confidence;
  return typeof c === "number" ? Math.min(c, DOC_CONFIDENCE_CAP) : 0.6;
}

/**
 * Map one recognized document to prefill-shaped ``ExtractedField``s.
 * Deliberately partial: only whitelisted identity/parcel fields; anything
 * the document doesn't carry is simply absent (never invented).
 */
export function recognizedDocToPrefill(
  doc: RecognizedDocument,
): Record<string, ExtractedField> {
  const out: Record<string, ExtractedField> = {};
  const file = doc._source_file ?? undefined;

  const put = (
    formField: string,
    value: unknown,
    confidence: number,
    note?: string,
  ) => {
    if (value == null || value === "") return;
    // First document to speak for a field wins within this doc's wave;
    // cross-document precedence is handled by fill-empty-only in the
    // draft writer.
    if (formField in out) return;
    out[formField] = {
      value,
      confidence,
      source: {
        file,
        extractor: "document_recognizer",
        note: note ?? `Iz dokumenta (${doc.document_type})`,
      },
    };
  };

  for (const [recKey, formField] of Object.entries(DIRECT_FIELD_MAP)) {
    put(formField, fieldValue(doc, recKey), fieldConfidence(doc, recKey));
  }

  // ZK owner as investitor suggestion — only where the doc is a
  // katastar/ZK extract AND it didn't name an investitor itself.
  if (doc.document_type === "katastar_vlasnicki") {
    const { multi } = scalarizeOwnerName(fieldValue(doc, "vlasnik_naziv"));
    const note =
      multi > 1
        ? `Više suvlasnika (${multi}) iz ZK izvatka — provjerite investitora i udjele.`
        : VLASNIK_NOTE;
    for (const [recKey, formField] of Object.entries(VLASNIK_FIELD_MAP)) {
      if (formField in out) continue; // direct investitor_* already spoke
      // Ime se spaja čitljivo; OIB/adresa uzimaju prvog suvlasnika (forma
      // ima jedno polje) — nikad array u polje.
      const raw = fieldValue(doc, recKey);
      const value = recKey === "vlasnik_naziv" ? scalarizeOwnerName(raw).value : firstOfList(raw);
      put(formField, value, Math.min(fieldConfidence(doc, recKey), 0.6), note);
    }
  }

  // Numeric coercion for the parcel area ("1 020,50" / "1020.5 m²" → number).
  const area = out["cestica_povrsina_m2"];
  if (area) {
    const n = coerceNumber(area.value);
    if (n == null) {
      delete out["cestica_povrsina_m2"];
    } else {
      area.value = n;
    }
  }

  return out;
}

function coerceNumber(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v !== "string") return null;
  const cleaned = v
    .replace(/m²|m2/gi, "")
    .replace(/\s/g, "")
    .replace(",", ".");
  const n = Number(cleaned);
  return Number.isFinite(n) && n > 0 ? n : null;
}
